import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useAuthStore } from '../../store/auth-store';
import {
  CrudDrawer,
  TextField,
  NumberField,
  SelectField,
  CheckField,
  Field,
  DeleteButton,
  extractErr,
} from '../../components/crud-drawer';

interface CommissionRule {
  id: string;
  name: string;
  scope: 'PLATFORM_DEFAULT' | 'SELLER';
  sellerId: string | null;
  commissionPct: string;
  commissionFlat: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
}

// PLATFORM_DEFAULT / SELLER only — the DB column still allows CATEGORY, but the
// API DTO deliberately dropped it: per-product pricing already varies through
// the fee percentages in Platform Config, and a second category-shaped rate
// table would only give two places to disagree.
const SCOPES = [
  { value: 'PLATFORM_DEFAULT', label: 'Platform default — every seller' },
  { value: 'SELLER', label: 'Specific seller — overrides the default' },
];

const EMPTY = {
  name: '',
  scope: 'PLATFORM_DEFAULT',
  sellerId: '',
  commissionPct: '',
  commissionFlat: '',
  effectiveFrom: '',
  effectiveTo: '',
  isActive: true,
};

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(v: string): string | null {
  return v ? new Date(v).toISOString() : null;
}

export function CommissionRulesTab() {
  const canWrite = useAuthStore((s) => s.hasScope)('catalog.commission.write');

  const [rows, setRows] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<CommissionRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const { confirm } = useConfirm();
  // Client-side: the API returns every rule. Matches each column shown.
  const { query, setQuery, filtered } = useTableSearch(rows, (r) => [
    r.name,
    r.scope === 'PLATFORM_DEFAULT' ? 'Platform default' : `Seller #${r.sellerId}`,
    `${Number(r.commissionPct).toFixed(2)}%`,
    Number(r.commissionFlat) > 0 ? `₹${Number(r.commissionFlat).toFixed(2)}` : null,
    new Date(r.effectiveFrom).toLocaleDateString('en-IN'),
    r.effectiveTo ? new Date(r.effectiveTo).toLocaleDateString('en-IN') : 'ongoing',
    r.isActive ? 'Active' : 'Inactive',
  ]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<CommissionRule[]>('/admin/commission-rules');
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => { setEditing(null); setCreating(true); setForm(EMPTY); };

  const openEdit = (row: CommissionRule) => {
    setCreating(false); setEditing(row);
    setForm({
      name: row.name,
      scope: row.scope,
      sellerId: row.sellerId ?? '',
      commissionPct: String(row.commissionPct),
      commissionFlat: String(row.commissionFlat),
      effectiveFrom: toLocalInput(row.effectiveFrom),
      effectiveTo: toLocalInput(row.effectiveTo),
      isActive: row.isActive,
    });
  };

  const close = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.scope === 'SELLER' && !form.sellerId.trim()) {
      toast.error('A seller-scoped rule needs a seller id'); return;
    }
    const pct = form.commissionPct === '' ? 0 : Number(form.commissionPct);
    const flat = form.commissionFlat === '' ? 0 : Number(form.commissionFlat);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error('Commission percent must be between 0 and 100'); return;
    }
    if (!Number.isFinite(flat) || flat < 0) {
      toast.error('Flat commission cannot be negative'); return;
    }
    if (form.effectiveFrom && form.effectiveTo && new Date(form.effectiveTo) <= new Date(form.effectiveFrom)) {
      toast.error('The end date must be after the start date'); return;
    }

    const body = {
      name: form.name.trim(),
      scope: form.scope,
      sellerId: form.scope === 'SELLER' ? form.sellerId.trim() : null,
      commissionPct: pct,
      commissionFlat: flat,
      ...(form.effectiveFrom ? { effectiveFrom: fromLocalInput(form.effectiveFrom) } : {}),
      effectiveTo: fromLocalInput(form.effectiveTo),
      isActive: form.isActive,
    };
    setBusy(true);
    try {
      if (creating) {
        await api.post('/admin/commission-rules', body);
        toast.success('Commission rule created');
      } else if (editing) {
        await api.put(`/admin/commission-rules/${editing.id}`, body);
        toast.success('Commission rule updated');
      }
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    const ok = await confirm({
      title: `Delete "${editing.name}"?`,
      description: 'This can\'t be undone. Past payouts already recorded their commission and are unaffected, but sellers pointing at this rule fall back to the platform default.',
      confirmLabel: 'Delete rule',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/commission-rules/${editing.id}`);
      toast.success('Commission rule deleted');
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row: CommissionRule, next: boolean) => {
    // Switching off hides it at once, so ask first; switching back on is
    // harmless and stays one click.
    if (!next) {
      const ok = await confirm({
        title: `Deactivate "${row.name}"?`,
        description: 'It stops applying to new sales. Past payouts already recorded their commission and are unaffected. You can switch it back on any time.',
        confirmLabel: 'Deactivate rule',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await api.put(`/admin/commission-rules/${row.id}`, { isActive: next });
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: !next } : r)));
    }
  };

  return (
    <>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-surface-line px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-ink-900">Commission rules</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-500">
              What the platform takes per sale, deducted from the seller's payout. Charged on
              the seller's own price, never on the platform markup.
            </p>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={openCreate}
              className="btn btn-primary btn-sm shrink-0"
            >
              <Symbol name="add" size={16} /> Add rule
            </button>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="border-b border-surface-line px-5 py-3">
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search by rule, scope, seller, rate, dates or status"
              resultCount={filtered.length}
            />
          </div>
        )}

        {loading ? (
          <SkeletonRows cols={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="percent"
            title="No commission rules yet"
            body="Sellers are charged nothing until a platform default exists."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for "${query.trim()}"`}
            body="Try a rule name, Platform default, a seller id, a rate, a date, or Active / Inactive."
            action={
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>
                Clear search
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rule</th>
                <th>Scope</th>
                <th className="!text-right">Rate</th>
                <th className="hidden lg:table-cell">Effective</th>
                <th className="!text-right">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={r.isActive ? '' : 'bg-surface-sunk'}>
                  <td className="font-medium text-ink-900">{r.name}</td>
                  <td className="text-ink-700">
                    {r.scope === 'PLATFORM_DEFAULT' ? (
                      <span className="badge badge-info">
                        Platform default
                      </span>
                    ) : (
                      <span className="text-ink-700">Seller #{r.sellerId}</span>
                    )}
                  </td>
                  <td className="text-right font-tabular font-semibold text-ink-900">
                    {Number(r.commissionPct).toFixed(2)}%
                    {Number(r.commissionFlat) > 0 && (
                      <span className="text-ink-500"> + ₹{Number(r.commissionFlat).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="hidden text-ink-500 lg:table-cell">
                    {new Date(r.effectiveFrom).toLocaleDateString('en-IN')}
                    {r.effectiveTo ? ` → ${new Date(r.effectiveTo).toLocaleDateString('en-IN')}` : ' → ongoing'}
                  </td>
                  <td>
                    <div className="flex justify-end">
                      <Toggle
                        checked={r.isActive}
                        disabled={!canWrite}
                        aria-label={`${r.isActive ? 'Deactivate' : 'Activate'} ${r.name}`}
                        onChange={(v) => void toggleActive(r, v)}
                      />
                    </div>
                  </td>
                  <td className="text-right">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="btn btn-ghost btn-sm"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <CrudDrawer
        open={creating || editing !== null}
        title={creating ? 'New commission rule' : `Edit ${editing?.name ?? ''}`}
        busy={busy}
        onClose={close}
        onSubmit={() => void save()}
        secondaryAction={editing ? <DeleteButton onClick={() => void remove()} disabled={busy} /> : undefined}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={(v) => setForm((f) => ({ ...f, name: v }))}
          placeholder="Standard marketplace commission"
          required
        />
        <SelectField
          label="Scope"
          value={form.scope}
          onChange={(v) => setForm((f) => ({ ...f, scope: v }))}
          options={SCOPES}
          required
        />
        {form.scope === 'SELLER' && (
          <TextField
            label="Seller ID"
            value={form.sellerId}
            onChange={(v) => setForm((f) => ({ ...f, sellerId: v }))}
            placeholder="42"
            mono
            required
            hint="Copy the id from the Sellers page."
          />
        )}
        <NumberField
          label="Commission percent"
          value={form.commissionPct}
          onChange={(v) => setForm((f) => ({ ...f, commissionPct: v }))}
          placeholder="10"
          step="0.01"
          min={0}
          max={100}
          hint="Percent of the seller's own price per order item."
        />
        <NumberField
          label="Flat commission (₹ per item)"
          value={form.commissionFlat}
          onChange={(v) => setForm((f) => ({ ...f, commissionFlat: v }))}
          placeholder="0"
          step="0.01"
          min={0}
          hint="Added on top of the percentage. Leave 0 for percentage only."
        />
        <Field label="Effective from" hint="Leave empty to start immediately.">
          <input
            type="datetime-local"
            value={form.effectiveFrom}
            onChange={(e) => setForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
            className="input"
          />
        </Field>
        <Field label="Effective until" hint="Leave empty for an open-ended rule.">
          <input
            type="datetime-local"
            value={form.effectiveTo}
            onChange={(e) => setForm((f) => ({ ...f, effectiveTo: e.target.value }))}
            className="input"
          />
        </Field>
        <CheckField
          label="Active"
          checked={form.isActive}
          onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
        />
      </CrudDrawer>
    </>
  );
}
